import {normalizePage, normalizePageSize} from "./paginationPreferences";

export const viewStorageKey = "admin-id-express-current-view";

export type SavedView = {
  section: string;
  resource: string;
  page: number;
  pageSize?: number;
  filterField?: string;
  filterValue?: string;
};

const safeViewPart = (value: unknown) => /^[a-zA-Z0-9_-]+$/.test(String(value || "")) ? String(value) : "";
const safeFilterField = (value: unknown) => value === "*" ? "*" : safeViewPart(value);
const safeFilterValue = (value: unknown) => typeof value === "string" ? value.slice(0, 500) : "";

const filterFromUrl = (url: URL) => {
  if (url.searchParams.has("filterField") || url.searchParams.has("filterValue")) {
    return {
      field: safeFilterField(url.searchParams.get("filterField")) || "*",
      value: safeFilterValue(url.searchParams.get("filterValue") || ""),
    };
  }
  if (url.searchParams.get("status")) {
    return {field: "status", value: safeFilterValue(url.searchParams.get("status"))};
  }
  return {
    field: safeFilterField(url.searchParams.get("searchField")) || "*",
    value: safeFilterValue(url.searchParams.get("search") || ""),
  };
};

const filterFromStorage = (stored: Record<string, any>) => {
  if (Object.prototype.hasOwnProperty.call(stored, "filterValue")) {
    return {field: safeFilterField(stored.filterField) || "*", value: safeFilterValue(stored.filterValue)};
  }
  if (stored.status) return {field: "status", value: safeFilterValue(stored.status)};
  if (Object.prototype.hasOwnProperty.call(stored, "search")) {
    return {field: safeFilterField(stored.searchField) || "*", value: safeFilterValue(stored.search)};
  }
  return null;
};

export const readSavedView = (): SavedView => {
  try {
    const url = new URL(window.location.href);
    const stored = JSON.parse(localStorage.getItem(viewStorageKey) || "{}");
    const urlSection = safeViewPart(url.searchParams.get("section"));
    const fromUrl = Boolean(urlSection);
    const result: SavedView = {
      section: urlSection || safeViewPart(stored.section) || "overview",
      resource: fromUrl ? safeViewPart(url.searchParams.get("resource")) : safeViewPart(stored.resource),
      page: normalizePage(fromUrl ? url.searchParams.get("page") : stored.page),
      pageSize: normalizePageSize(fromUrl ? url.searchParams.get("pageSize") : stored.pageSize),
    };
    if (result.section === "kyc" && result.resource === "verification") {
      result.section = "personal_data";
    }
    const filter = fromUrl ? filterFromUrl(url) : filterFromStorage(stored);
    if (fromUrl || filter) {
      result.filterField = filter?.field || "*";
      result.filterValue = filter?.value || "";
    }
    return result;
  } catch { return {section: "overview", resource: "", page: 1, pageSize: 25}; }
};

export const saveView = (view: SavedView) => {
  try {
    localStorage.setItem(viewStorageKey, JSON.stringify(view));
    const url = new URL(window.location.href);
    url.searchParams.set("section", view.section);
    view.resource ? url.searchParams.set("resource", view.resource) : url.searchParams.delete("resource");
    view.page > 1 ? url.searchParams.set("page", String(view.page)) : url.searchParams.delete("page");
    view.pageSize && view.pageSize !== 25 ? url.searchParams.set("pageSize", String(view.pageSize)) : url.searchParams.delete("pageSize");
    view.filterValue ? url.searchParams.set("filterField", view.filterField || "*") : url.searchParams.delete("filterField");
    view.filterValue ? url.searchParams.set("filterValue", view.filterValue) : url.searchParams.delete("filterValue");
    url.searchParams.delete("search");
    url.searchParams.delete("searchField");
    url.searchParams.delete("status");
    window.history.replaceState(window.history.state, "", url);
  } catch { /* Navigation remains functional when storage or History API is unavailable. */ }
};
